import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, initials } from "@/lib/format";
import ThemeToggle from "@/components/ThemeToggle";
import { ChangePasswordDialog } from "@/components/PasswordDialog";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserRound,
  Flame,
  CalendarClock,
  FileBarChart,
  History,
  LogOut,
  Landmark,
  KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin", "manager"], end: true },
  { to: "/branches", label: "Branches", icon: Building2, roles: ["super_admin"] },
  { to: "/managers", label: "Managers", icon: Users, roles: ["super_admin", "admin"] },
  { to: "/clients", label: "Clients", icon: UserRound, roles: ["super_admin", "admin", "manager"] },
  { to: "/cross-sell", label: "Cross-Sell", icon: Flame, roles: ["super_admin", "admin", "manager"] },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, roles: ["super_admin", "admin", "manager"] },
  { to: "/reports", label: "Reports", icon: FileBarChart, roles: ["super_admin", "admin", "manager"] },
  { to: "/audit", label: "Audit", icon: History, roles: ["super_admin"] },
];

export default function Layout() {
  const { user, branch, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className="sidebar-bg w-64 shrink-0 hidden md:flex flex-col border-r border-border sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="px-6 py-6 flex items-center gap-3 border-b border-border">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
            <Landmark className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-none text-foreground">Wealthoo</div>
            <div className="text-xs text-gold tracking-wider">360° WEALTH CRM</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium row-hover ${
                  isActive
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-semibold num">
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{branch?.name || "Head Office"}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="glass sticky top-0 z-30 border-b border-border px-5 md:px-8 py-3.5 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</div>
            <div className="font-display font-semibold text-base">
              {branch ? `${branch.name} Branch` : "Organization Overview"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <ChangePasswordDialog
              trigger={
                <button
                  data-testid="header-change-password"
                  title="Change password"
                  className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary row-hover border border-border"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
              }
            />
            <span
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/15 text-primary border border-primary/30 hidden sm:inline"
              data-testid="role-badge"
            >
              {ROLE_LABELS[user.role]}
            </span>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 row-hover"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        <main className="flex-1 px-5 md:px-8 py-6 max-w-[1500px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
